import { Theme } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

interface StyleProperties {
  backgroundColor: string;
  backgroundColorHover: string;
  color: string;
}

const getProperties = (theme: Theme, active: boolean): StyleProperties => {
  if (active) {
    return {
      backgroundColor: theme.palette.common.blue50,
      backgroundColorHover: theme.palette.common.brandSelectedHover,
      color: theme.palette.common.blue500,
    };
  }
  return {
    backgroundColor: theme.palette.common.gray100,
    backgroundColorHover: theme.palette.common.gray200,
    color: theme.palette.common.gray800,
  };
};

// The theme arrives through `ThemeProvider` rather than a module import, so a
// consumer that maps the palette — recruiter-ui maps it onto Ripple — reaches
// these values. A static import resolves once at load and cannot.
const useStyles = makeStyles<Theme, boolean>(theme => ({
  arrow: {
    marginLeft: theme.spacing(1),
  },
  btn: btnActive => {
    const style = getProperties(theme, btnActive);

    return {
      // Only the colors carry into the selected state. Spreading the whole
      // object emitted `background-color-hover`, which is not a property.
      '&.Mui-selected': {
        backgroundColor: style.backgroundColor,
        color: style.color,
      },
      // Material-UI ships `.MuiToggleButton-root.Mui-selected:hover` at (0,3,0),
      // which outranks a plain `&:hover` and washes the button with a black
      // alpha. Matching its specificity keeps the selected tone.
      '&.Mui-selected:hover': {
        backgroundColor: style.backgroundColorHover,
      },
      '&:hover': {
        backgroundColor: style.backgroundColorHover,
        cursor: 'pointer',
      },
      backgroundColor: style.backgroundColor,
      borderRadius: 100,
      color: style.color,
      cursor: 'pointer',
      fontFamily: theme.typography.overline.fontFamily,
      fontSize: theme.typography.overline.fontSize,
      fontWeight: 'normal',
      height: 28,
      lineHeight: theme.typography.overline.lineHeight,
      marginRight: theme.spacing(1.25),
      padding: `${theme.spacing(0.5)}px ${theme.spacing(1.5)}px`,
      textDecoration: 'none',
      textTransform: 'none',
    };
  },
  checkmark: {
    marginLeft: 18,
  },
  menu: {
    transform: `translateY(${theme.spacing(1)}px)`,
  },
  // Material-UI's own `MuiToggleButton` draws a 1px black-alpha border. The
  // filter controls beside this one carry no border, so the pill drops it.
  // The dropdown keeps it: its blue fill is 1.09:1 against white, and the
  // border is the only thing marking its edge.
  pill: {
    '&.Mui-focusVisible': {
      outline: '2px auto rgba(19,124,189,.6)',
      outlineOffset: 2,
    },
    border: 'none',
  },
  text: {
    color: theme.palette.common.gray800,
  },
}));

export default useStyles;
